import { Fragment, useCallback, useMemo } from 'react';
import { Card } from '@quero-delivery/quero-components-web';
import isEmpty from 'lodash/isEmpty';
import keys from 'lodash/keys';
import Tooltip from 'rc-tooltip';
import { useNavigate } from 'react-router';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash } from '@fortawesome/pro-solid-svg-icons';
import useMotoboyDedication from '@/hooks/motoboy/useMotoboyDedication';

import type { IPlaceMinimal } from '@/hooks/place/usePlacesMinimal';
import usePlacesMinimal from '@/hooks/place/usePlacesMinimal';
import { getAcronym } from '@/Utils/FnUtils';

import styles from './styles.module.scss';
import { dialog } from '@/infra/dialog/Dialog';
import { AccessPermission } from '@/_components/access-permission';

interface Props {
  motoboyId: string;
}

function DedicationInfo({ motoboyId }: Props) {
  const navigate = useNavigate();

  const {
    getDedicationByPlaceId,
    dedicationIsLoading,
    placesDedicated,
    handleDeleteDedicationByPlaceId,
  } = useMotoboyDedication(motoboyId);

  const { placesMinimal } = usePlacesMinimal();

  const placesInfo = useMemo(() => {
    if (isEmpty(placesDedicated) || isEmpty(placesMinimal)) {
      return [];
    }

    const newPlacesInfo = placesDedicated.map(
      id => placesMinimal.find(place => place._id === id) || null,
    );

    return newPlacesInfo;
  }, [placesDedicated, placesMinimal]);

  async function handleDeleteDedication(placeInfo: IPlaceMinimal) {
    const confirmed = await dialog.confirm(
      <div className="text-center">
        <p className="mgn-bottom-sm">
          Você está tentando excluir o <b>{placeInfo.nomeExibicao}</b> da
          dedicação do entregador
        </p>
        <h3>Você tem certeza que quer excluir?</h3>
      </div>,
    );

    if (!confirmed) {
      return;
    }

    await handleDeleteDedicationByPlaceId(placeInfo);
  }

  function handleEditDedication() {
    navigate(`dedicar`);
  }

  const renderPlaceDedicationInfo = useCallback(
    (place: IPlaceMinimal) => {
      const placeDedication = getDedicationByPlaceId(place._id);

      if (!placeDedication) {
        return null;
      }

      return (
        <div className={styles.placeDedication}>
          <b>{place.nomeExibicao}</b>

          <div>
            <div className={styles.containerDays}>
              {keys(placeDedication).map(weekDay => {
                const weekDayData = placeDedication[weekDay];

                return (
                  <Tooltip
                    key={weekDay}
                    placement="top"
                    mouseEnterDelay={0.5}
                    overlay={
                      <div className="flex-col">
                        {weekDayData.map((time, index) => (
                          <span key={`${weekDay}${index}`}>
                            {time.horaInicial} / {time.horaFinal}
                          </span>
                        ))}
                      </div>
                    }
                  >
                    <span>{getAcronym(weekDay)} / </span>
                  </Tooltip>
                );
              })}
            </div>

            <AccessPermission permissionKey="deliveryman.dedicateDeliveryman">
              <div className={styles.containerIcons}>
                <FontAwesomeIcon
                  data-testid="icon-edit"
                  icon={faEdit}
                  onClick={handleEditDedication}
                  className={styles.icons}
                />
                <FontAwesomeIcon
                  data-testid="icon-trash"
                  icon={faTrash}
                  onClick={() => handleDeleteDedication(place)}
                  className={styles.icons}
                />
              </div>
            </AccessPermission>
          </div>
        </div>
      );
    },
    [getDedicationByPlaceId],
  );

  return (
    <Card type="none" className={styles.card} loading={dedicationIsLoading}>
      {placesInfo.map(placeInfo => {
        if (!placeInfo) {
          return null;
        }

        return (
          <Fragment key={placeInfo._id}>
            {renderPlaceDedicationInfo(placeInfo)}
          </Fragment>
        );
      })}
    </Card>
  );
}

export default DedicationInfo;
